import React, { useState, useCallback, useEffect, useRef } from 'react'
import { ArrowLeft, Volume2, VolumeX, Activity } from 'lucide-react'
import useWebSocket from '../hooks/useWebSocket'

const WS_URL = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api/nim`

interface AudienceViewProps {
  token: string
  onBack: () => void
  sourceLang?: string
  targetLang?: string
}

interface TranslationMessage {
  id: number
  text: string
  audio?: string
  timestamp: number
}

const AudienceView: React.FC<AudienceViewProps> = ({ token, onBack, sourceLang: _sourceLang = 'vi', targetLang: _targetLang = 'en' }) => {
  const [messages, setMessages] = useState<TranslationMessage[]>([])
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'error'>('disconnected')
  const [isMuted, setIsMuted] = useState(false)
  const [presenterActive, setPresenterActive] = useState(false)
  const messageIdRef = useRef(0)
  const audioContextRef = useRef<AudioContext | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const handleWebSocketMessage = useCallback((data: any) => {
    if (data.type === 'translation') {
      const newMessage: TranslationMessage = {
        id: messageIdRef.current++,
        text: data.text || '',
        audio: data.audio,
        timestamp: Date.now(),
      }
      
      setMessages(prev => [...prev, newMessage])
      
      // Auto-play audio if not muted
      if (data.audio && !isMuted) {
        playAudio(data.audio)
      }
    } else if (data.type === 'status') {
      setPresenterActive(data.presenter_active || false)
    } else if (data.type === 'heartbeat') {
      // Keep connection alive
    }
  }, [isMuted])

  const { status: wsStatus } = useWebSocket({
    url: `${WS_URL}/ws/audience?token=${token}`,
    onMessage: handleWebSocketMessage,
  })

  useEffect(() => {
    setConnectionStatus(wsStatus)
  }, [wsStatus])

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const playAudio = useCallback((base64Audio: string) => {
    try {
      // Initialize AudioContext on first use
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext()
      }

      const audioData = atob(base64Audio)
      const arrayBuffer = new ArrayBuffer(audioData.length)
      const view = new Uint8Array(arrayBuffer)
      for (let i = 0; i < audioData.length; i++) {
        view[i] = audioData.charCodeAt(i)
      }

      audioContextRef.current.decodeAudioData(arrayBuffer, (buffer) => {
        const source = audioContextRef.current!.createBufferSource()
        source.buffer = buffer
        source.connect(audioContextRef.current!.destination)
        source.start(0)
      })
    } catch (err) {
      console.error('Audio playback error:', err)
    }
  }, [])

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev)
  }, [])

  return (
    <div className="audience-view">
      <div className="audience-header">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={24} />
        </button>
        <div className="header-info">
          <span className={`presenter-status ${presenterActive ? 'active' : ''}`}>
            {presenterActive ? '● Presenter Active' : '○ Waiting for presenter'}
          </span>
          <div className="status-indicator">
            <Activity size={16} />
            <span className={`status-${connectionStatus}`}>
              {connectionStatus}
            </span>
          </div>
        </div>
        <button className="mute-btn" onClick={toggleMute}>
          {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
        </button>
      </div>

      <div className="audience-content">
        {messages.length === 0 && !presenterActive && (
          <div className="waiting-message">
            <p>Waiting for presenter to start...</p>
          </div>
        )}
        
        {messages.length === 0 && presenterActive && (
          <div className="waiting-message">
            <p>Presenter is speaking... Translation will appear here</p>
          </div>
        )}

        <div className="messages-list">
          {messages.map((message) => (
            <div key={message.id} className="message fade-in">
              <p className="message-text">{message.text}</p>
              {message.audio && (
                <button
                  className="replay-btn"
                  onClick={() => playAudio(message.audio!)}
                >
                  <Volume2 size={16} />
                </button>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  )
}

export default AudienceView
